import { useState, type ReactNode } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../Dialog/Dialog'
import { Button } from '../Button/Button'
import { ColorPopover, rgba } from './WorkbenchColorPicker'
import { BitsPopover } from './WorkbenchFloatingBits'

// Settings for the faded volume bars. Two colours (up / down) with opacity,
// applied live. Same Dialog + ColorPopover controls used elsewhere. Reset
// restores the theme defaults.

export interface WorkbenchVolumeSettingsProps {
  up: string
  down: string
  onChange: (patch: { up?: string; down?: string }) => void
  onReset: () => void
  onClose: () => void
}

/** Parse a stored colour (hex or rgba) into the { hex, op } the picker expects. */
function parseColor(c: string | undefined): { hex: string; op: number } {
  if (c && c.startsWith('rgba')) {
    const m = c.match(/[\d.]+/g) ?? []
    const [r = '38', g = '166', b = '154', a = '1'] = m
    const hex =
      '#' + [r, g, b].map((n) => Math.round(Number(n)).toString(16).padStart(2, '0')).join('')
    return { hex, op: Math.round((Number(a) || 1) * 100) }
  }
  return { hex: c || '#26a69a', op: 100 }
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 min-h-8">
      <span className="text-body text-muted-foreground">{label}</span>
      <div className="flex items-center gap-2">{children}</div>
    </div>
  )
}

function ColorField({ value, onChange }: { value: string; onChange: (c: string) => void }) {
  const [open, setOpen] = useState(false)
  const { hex, op } = parseColor(value)
  return (
    <BitsPopover
      open={open}
      onOpenChange={setOpen}
      align="right"
      anchor={
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label="Colour"
          className="h-7 w-10 rounded-md border border-border cursor-pointer"
          style={{ background: value }}
        />
      }
    >
      <ColorPopover
        curColor={hex}
        curOpacity={op}
        onColor={(c) => onChange(rgba(c, op))}
        onOpacity={(v) => onChange(rgba(hex, v))}
      />
    </BitsPopover>
  )
}

export function WorkbenchVolumeSettings({
  up,
  down,
  onChange,
  onReset,
  onClose,
}: WorkbenchVolumeSettingsProps) {
  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) onClose()
      }}
    >
      <DialogContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        overlayClassName="bg-black/60"
        className="v1 bg-popover max-w-[420px] sm:max-w-[420px] w-[92vw] p-0 gap-0 overflow-hidden flex flex-col max-h-[85dvh]"
      >
        <DialogHeader className="px-4 pt-4 pb-3 border-b border-border shrink-0">
          <DialogTitle className="text-section text-foreground">Volume</DialogTitle>
        </DialogHeader>

        <div className="px-4 py-3 flex-1 min-h-0 overflow-y-auto">
          <div className="text-eyebrow text-muted-foreground mb-2">Bars</div>
          <div className="flex flex-col gap-1">
            <Row label="Up colour">
              <ColorField value={up} onChange={(c) => onChange({ up: c })} />
            </Row>
            <Row label="Down colour">
              <ColorField value={down} onChange={(c) => onChange({ down: c })} />
            </Row>
          </div>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-border shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={onReset}
            className="px-2 text-muted-foreground hover:text-foreground"
          >
            Reset to default
          </Button>
          <Button size="sm" onClick={onClose}>
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
